import { ContentPreview } from "@turtlewave/types";
import { RowsPhotoAlbum, Photo } from "react-photo-album";
import "react-photo-album/rows.css";

import { Loadable } from "../../types/loadable";

import { ContentCard } from "./ContentCard";

interface ContentPhoto extends Photo {
  content: ContentPreview;
}

interface ContentListProps {
  query: Loadable<ContentPreview[]>;
  compilationId?: string;
}

// Simple seeded PRNG (mulberry32) for deterministic skeleton dimensions
function seededRandom(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SKELETON_COUNT = 10;
const SKELETON_SEED = 42;

function ContentListSkeleton() {
  const rand = seededRandom(SKELETON_SEED);
  const items = Array.from({ length: SKELETON_COUNT }, (_, i) => {
    const aspectRatio = 0.7 + rand() * 0.7; // 0.7 to 1.4
    return { id: i, aspectRatio };
  });

  return (
    <div className="flex flex-wrap gap-3 sm:gap-4 lg:gap-5">
      {items.map((item) => (
        <div
          key={item.id}
          className="skeleton-light-bg animate-pulse rounded-xs"
          style={{
            aspectRatio: item.aspectRatio,
            flexGrow: item.aspectRatio,
            flexBasis: `${item.aspectRatio * 200}px`,
          }}
        />
      ))}
    </div>
  );
}

export function ContentList({ query, compilationId }: ContentListProps) {
  if (query.isLoading) {
    return <ContentListSkeleton />;
  }

  const contentPreviews = query.data;
  if (!contentPreviews || contentPreviews.length === 0) {
    return null;
  }

  const photos: ContentPhoto[] = contentPreviews.map((content) => ({
    src: content.preview_url ?? "",
    width: content.preview_width,
    height: content.preview_height,
    key: content.content_id,
    alt: content.title,
    content,
  }));

  return (
    <RowsPhotoAlbum
      photos={photos}
      defaultContainerWidth={1200}
      targetRowHeight={(containerWidth) => {
        if (containerWidth >= 1024) return 350;
        if (containerWidth >= 640) return 275;
        return 200;
      }}
      spacing={(containerWidth) => {
        if (containerWidth >= 1024) return 20;
        if (containerWidth >= 640) return 16;
        return 12;
      }}
      render={{
        wrapper: ({ style, ...rest }, { photo, width, height }) => (
          <div
            style={{ ...style, aspectRatio: `${width} / ${height}` }}
            {...rest}
          >
            <ContentCard
              content={photo.content}
              sizeClassName="w-full h-full"
              compilationId={compilationId}
            />
          </div>
        ),
      }}
    />
  );
}
